import { Component, computed, inject, input, model, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { DrawCodesService } from '../../../services/draw-codes.service';
import { CraftType, DropdownData, GenericDropdownComponent } from '@zayan-erp/shared';
import { rxResourceFixed } from '@zayan-erp/shared';
import { of, tap } from 'rxjs';
import { CraftEntityService } from '../../../services/craft-entity.service';
import { ProductService } from '../../../services/product.service';
import { ToggleSwitchModule } from '@openng/optimus-ui/toggleswitch';
import { ProductCraftService } from '../../../services/product-craft.service';
import { productCraft } from '../../../models/product-craft.model';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'lib-product-crafts-form',
  imports: [ReactiveFormsModule, NgSelectModule, GenericDropdownComponent, ToggleSwitchModule],
  templateUrl: './product-crafts-form.component.html',
  styleUrl: './product-crafts-form.component.scss'
})
export class ProductCraftsFormComponent {
  #drawCodeService = inject(DrawCodesService);
  #craftEntityService = inject(CraftEntityService);
  #productService = inject(ProductService);
  #productCraftService = inject(ProductCraftService);
  #modalService = inject(NgbModal);

  CraftTypeEnum = CraftType;
  selectedCraftId = model<string | null>(null);
  productId = input<string | null>();
  productCraftId = input<string | null>(null);
  reload = output<void>();

  drawCode = toSignal(this.#drawCodeService.readDrawCodes());
  drawCodeList = computed(
    () =>
      this.drawCode()?.map(
        item => ({ id: item.id, firstLabel: item.code, secondLabel: item.description }) as DropdownData
      ) ?? []
  );

  craftForm = new FormGroup({
    id: new FormControl<string | null>(null),
    productId: new FormControl<string | null>(this.productId() ?? null, [Validators.required]),
    craftId: new FormControl<string | null>(null, [Validators.required]),
    craftyType: new FormControl<number | null>(null),
    poDescription: new FormControl<string | null>(null, [Validators.required]),
    drawCodeId: new FormControl<string | null>(null),
    taskId: new FormControl<string | null>(null),
    excludeFromGlobalMarkup: new FormControl<boolean>(false)
  });

  productRxresource = rxResourceFixed({
    params: () => ({ id: this.productId() }),
    defaultValue: null,
    stream: ({ params }) => {
      if (params.id) {
        return this.#productService.readProductById(params.id).pipe(
          tap(product => {
            this.craftForm.patchValue({
              poDescription: product.salesDescription,
              productId: product.id
            });
          })
        );
      }
      return of(null);
    }
  });
  craftRxresource = rxResourceFixed({
    params: () => ({ id: this.selectedCraftId() }),
    defaultValue: null,
    stream: ({ params }) => {
      if (params.id) {
        return this.#craftEntityService.readCraftEntityById(params.id).pipe(
          tap(craftEntity => {
            this.editForm(craftEntity);
          })
        );
      }
      return of(null);
    }
  });
  craftList = computed(() => this.craftRxresource.value());
  editForm(craftEntity: any) {
    this.craftForm.patchValue({
      craftId: craftEntity?.id,
      craftyType: craftEntity?.type,
      taskId: craftEntity?.task?.id
    });
  }
  productCraftRxresource = rxResourceFixed({
    params: () => ({ productCraftId: this.productCraftId() }),
    defaultValue: null,
    stream: ({ params }) => {
      if (params.productCraftId) {
        return this.#productCraftService.readProductCraftsbyId(params.productCraftId).pipe(
          tap((productCraft: any) => {
            this.craftForm.patchValue({
              id: productCraft.id,
              poDescription: productCraft.poDescription,
              productId: productCraft.productId,
              craftId: productCraft.craftId,
              craftyType: productCraft.craft.type,
              taskId: productCraft.craft.taskId,
              excludeFromGlobalMarkup: productCraft.excludeFromGlobalMarkup,
              drawCodeId: productCraft.drawCodeId
            });
          })
        );
      }
      return of(null);
    }
  });

  submitProductCraft() {
    if (!this.craftForm.valid) {
      this.craftForm.markAllAsTouched();
      return;
    }
    if (this.productCraftId()) {
      this.#productCraftService.updateProductCraft(this.craftForm.value as productCraft).subscribe({
        next: () => {
          this.afterSubmitSuccess();
        }
      });
    } else {
      this.#productCraftService.createProductCraft(this.craftForm.value as productCraft).subscribe({
        next: () => {
          this.afterSubmitSuccess();
        }
      });
    }
  }
  afterSubmitSuccess() {
    this.craftForm.reset();
    this.#modalService.dismissAll();
    this.reload.emit();
    this.selectedCraftId.set(null);
  }
}
